import { useEffect, useMemo, useState } from 'react';
import { EventMapItem } from '../types/Event';
import { useBrowserLocation } from './useBrowserLocation';

// Mapbox route optimization supports up to 10 destination events.
export const MAX_OPTIMIZATION_EVENTS = 10;

export function useOptimizedRoutePermissions(events: EventMapItem[]) {
  const { coords, isLoadingLocation, locationFailed, requestLocation } = useBrowserLocation();

  // Tracks whether route optimization is currently enabled.
  const [isOptimizeActive, setIsOptimizeActive] = useState(false);

  // Route optimization requires between 1 and 10 events.
  const hasValidEventCount = events.length >= 1 && events.length <= MAX_OPTIMIZATION_EVENTS;

  // Optimization can only run once the browser has provided a location.
  const canOptimizeRoute = !!coords && hasValidEventCount;

  // Automatically disable optimization whenever it becomes invalid.
  // Example:
  // - User optimizes 5 events.
  // - Filters change to 20 events.
  // - Optimization turns off automatically.
  useEffect(() => {
    if (!canOptimizeRoute && isOptimizeActive) {
      setIsOptimizeActive(false);
    }
  }, [canOptimizeRoute, isOptimizeActive]);

  const handleOptimizeClick = async () => {
    // Ignore clicks while requesting location or after location fails.
    if (isLoadingLocation || locationFailed || !hasValidEventCount) {
      return;
    }

    // Request the user's location the first time optimization is enabled.
    if (!coords) {
      const location = await requestLocation();

      // Automatically enable optimization once location is available.
      if (location) {
        setIsOptimizeActive(true);
      }

      return;
    }

    // Toggle optimization on/off.
    setIsOptimizeActive((previous) => !previous);
  };

  // Tooltip text reflects the current optimization state.
  const tooltipTitle = useMemo(() => {
    if (locationFailed) {
      return 'Unable to load location. Cannot perform route optimization without a starting point.';
    }

    if (events.length < 1) {
      return 'At least 1 event is required.';
    }

    if (events.length > MAX_OPTIMIZATION_EVENTS) {
      return `Route optimization is available for up to ${MAX_OPTIMIZATION_EVENTS} events.`;
    }

    return 'Optimize Route';
  }, [locationFailed, events.length]);

  return {
    userLocationCoords: coords,
    tooltipTitle,
    canOptimizeRoute,
    isOptimizeActive: isOptimizeActive && canOptimizeRoute,
    isLoadingLocation,
    handleOptimizeClick,
  };
}
